import * as PIXI from "pixi.js";

export const PLAYER_TOKEN_NAME = "PlayerToken";

const SQUELCH_FLAG = "__ET_SUPPRESS_RESTARTS";
const setSquelch = (on) => { try { if (on) window[SQUELCH_FLAG] = true; else delete window[SQUELCH_FLAG]; } catch {} };

export function buildToken(app, startPos, onChange, initialPxPerMeter = 50) {
  const layer = new PIXI.Container();
  layer.name = `${PLAYER_TOKEN_NAME}Layer`;
  layer.zIndex = 900;

  try {
    app.view.style.touchAction = "none";
    app.view.style.userSelect = "none";
    app.view.oncontextmenu = (e) => e.preventDefault();
  } catch {}

  const token = new PIXI.Container();
  token.name = PLAYER_TOKEN_NAME;
  token.eventMode = "static";
  token.cursor = "grab";
  token.position.set(startPos?.x ?? 0, startPos?.y ?? 0);
  token.hitArea = new PIXI.Circle(0, 0, 1);

  const circle = new PIXI.Graphics();
  token.addChild(circle);

  const inner = new PIXI.Graphics();
  inner.beginFill(0xffffff, 1);
  inner.drawCircle(0, 0, 3);
  inner.endFill();
  token.addChild(inner);

  const label = new PIXI.Text("PLAYER", {
    fill: 0x9beafe,
    fontSize: 10,
    fontFamily: "monospace",
  });
  label.anchor.set(0.5, 0);
  label.position.set(0, 18);
  token.addChild(label);

  layer.addChild(token);

  let currentPxPerMeter = initialPxPerMeter;
  let dragging = false;
  let pointerId = null;
  let dragDX = 0;
  let dragDY = 0;

  const updateTokenSize = (newPxPerMeter) => {
    if (!Number.isFinite(newPxPerMeter) || newPxPerMeter <= 0) return;

    currentPxPerMeter = newPxPerMeter;

    const radius = currentPxPerMeter / 2;
    const labelOffset = radius + 2;

    circle.clear();
    try { circle.lineStyle({ width: 2, color: 0x00d4ff, alpha: 1 }); }
    catch { circle.lineStyle(2, 0x00d4ff, 1); }
    circle.beginFill(0x003244, 0.9);
    circle.drawCircle(0, 0, radius);
    circle.endFill();

    token.hitArea.radius = radius;
    token.hitArea.x = 0;
    token.hitArea.y = 0;
    
    label.position.set(0, labelOffset);
  };

  updateTokenSize(initialPxPerMeter);

  let raf = 0;
  let pending = null;
  const flush = () => {
    raf = 0;
    const p = pending; pending = null;
    if (!p) return;
    try {
      window.dispatchEvent(new CustomEvent("EmitterTool:TokenDrag", {
        detail: { ...p, name: PLAYER_TOKEN_NAME, tokenMove: true, source: "token", reason: "token-move" },
      }));
    } catch {}
  };
  const schedule = (p) => {
    pending = p;
    if (!raf) raf = requestAnimationFrame(flush);
  };

  const toPixi = (domEvt) => {
    const rect = app.view.getBoundingClientRect();
    const x = ((domEvt.clientX - rect.left) * app.renderer.width) / rect.width;
    const y = ((domEvt.clientY - rect.top) * app.renderer.height) / rect.height;
    return { x, y };
  };

  let safetyTimer = null;
  const armSafetyTimer = () => {
    clearTimeout(safetyTimer);
    safetyTimer = setTimeout(() => endDrag(true), 3500);
  };

  const onWindowMove = (e) => {
    if (!dragging) return;
    if (e.pointerId != null && e.pointerId !== pointerId) return;
    const { x, y } = toPixi(e);
    const nx = x + dragDX;
    const ny = y + dragDY;
    token.position.set(nx, ny);
    token.cursor = "grabbing";
    schedule({ x: nx, y: ny });
    armSafetyTimer();
  };

  const endDrag = (emitFinal) => {
    if (!dragging) return;
    dragging = false;
    token.cursor = "grab";
    try {
      if (pointerId != null && app.view.hasPointerCapture?.(pointerId)) {
        app.view.releasePointerCapture(pointerId);
      }
    } catch {}
    pointerId = null;

    window.removeEventListener("pointermove", onWindowMove, { passive: true });
    window.removeEventListener("pointerup", onWindowUp, { passive: true });
    window.removeEventListener("pointercancel", onWindowCancel, { passive: true });
    window.removeEventListener("blur", onWindowBlur);
    window.removeEventListener("keydown", onKeyDown);

    clearTimeout(safetyTimer);
    safetyTimer = null;

    if (raf) { cancelAnimationFrame(raf); raf = 0; }

    setSquelch(false);

    if (emitFinal) {
      const p = { x: token.x, y: token.y };
      try { onChange?.(p); } catch {}
      try {
        window.dispatchEvent(new CustomEvent("EmitterTool:TokenDrop", {
          detail: { ...p, name: PLAYER_TOKEN_NAME, tokenMove: true, source: "token", reason: "token-move" },
        }));
      } catch {}
    }
  };

  const onWindowUp = (e) => {
    if (e.pointerId != null && e.pointerId !== pointerId) return;
    endDrag(true);
  };
  const onWindowCancel = (e) => {
    if (e.pointerId != null && e.pointerId !== pointerId) return;
    endDrag(true);
  };
  const onWindowBlur = () => endDrag(true);
  const onKeyDown = (e) => { if (e.key === "Escape") endDrag(false); };

  const onDown = (e) => {
    e.stopPropagation();
    try { e.preventDefault?.(); e.originalEvent?.preventDefault?.(); } catch {}

    dragging = true;
    pointerId = e.pointerId ?? e.data?.originalEvent?.pointerId ?? null;

    try { if (pointerId != null) app.view.setPointerCapture?.(pointerId); } catch {}

    token.cursor = "grabbing";

    const g = e.global;
    dragDX = token.x - g.x;
    dragDY = token.y - g.y;

    setSquelch(true);

    window.addEventListener("pointermove", onWindowMove, { passive: true });
    window.addEventListener("pointerup", onWindowUp, { passive: true });
    window.addEventListener("pointercancel", onWindowCancel, { passive: true });
    window.addEventListener("blur", onWindowBlur);
    window.addEventListener("keydown", onKeyDown);

    try {
      window.dispatchEvent(new CustomEvent("EmitterTool:TokenGrab", {
        detail: { x: token.x, y: token.y, name: PLAYER_TOKEN_NAME, tokenMove: true, source: "token", reason: "token-move" },
      }));
    } catch {}

    armSafetyTimer();
  };

  token.on("pointerdown", onDown);

  return {
    layer,
    getPos: () => ({ x: token.x, y: token.y }),
    setPos: (p) => { if (p) token.position.set(p.x, p.y); },
    getName: () => PLAYER_TOKEN_NAME,
    setPxPerMeter: updateTokenSize,
    destroy: () => {
      token.off("pointerdown", onDown);
      endDrag(false);
      try { layer.destroy({ children: true }); } catch {}
    },
  };
}